/**
 * Numeric validation/clamping/snapping, ported from Base UI's
 * `number-field/utils/validate.ts`. Snapping happens BEFORE clamping so
 * non-step-aligned boundaries stay reachable.
 */

import { getFormatter } from './format.js'
import { parseNumber } from './parse.js'

const STEP_EPSILON_FACTOR = 1e-10
// Matches Intl.NumberFormat's decimal maximumFractionDigits default.
const DEFAULT_DIGITS = 3

// Model NumberFormat v3 rounding options locally (es2022 Intl types omit them).
type NumberFormatOptionsWithRounding = Intl.NumberFormatOptions & {
  roundingIncrement?: number | undefined
  roundingMode?: string | undefined
  roundingPriority?: string | undefined
}

export function clamp(
  val: number,
  min: number = Number.MIN_SAFE_INTEGER,
  max: number = Number.MAX_SAFE_INTEGER,
): number {
  return Math.max(min, Math.min(val, max))
}

export function hasNumberFormatRoundingOptions(
  format?: NumberFormatOptionsWithRounding,
): format is NumberFormatOptionsWithRounding {
  return (
    format?.maximumFractionDigits != null
    || format?.minimumFractionDigits != null
    || format?.maximumSignificantDigits != null
    || format?.minimumSignificantDigits != null
    || format?.roundingIncrement != null
    || format?.roundingMode != null
    || format?.roundingPriority != null
  )
}

export function removeFloatingPointErrors(value: number, format?: NumberFormatOptionsWithRounding) {
  if (!Number.isFinite(value)) {
    return value
  }

  if (!hasNumberFormatRoundingOptions(format)) {
    return Number(value.toFixed(DEFAULT_DIGITS))
  }

  const formatter = getFormatter('en-US', {
    ...format,
    // These options alter only display decoration, not numeric rounding.
    signDisplay: 'auto',
    currencySign: 'standard',
    notation: format.notation === 'compact' ? 'standard' : format.notation,
    useGrouping: false,
  } as NumberFormatOptionsWithRounding)
  const roundedText = formatter.format(value)
  const roundedValue = parseNumber(roundedText, 'en-US', format)

  if (roundedValue === null) {
    return value
  }

  return formatter.format(roundedValue) === roundedText ? roundedValue : value
}

function snapToStep(
  value: number,
  base: number,
  step: number,
  mode: 'directional' | 'nearest' = 'directional',
) {
  const stepSize = Math.abs(step)
  const direction = Math.sign(step)
  const tolerance = stepSize * STEP_EPSILON_FACTOR * direction
  const rawSteps = value - base + tolerance

  if (mode === 'nearest') {
    return base + Math.round(rawSteps / step) * step
  }

  const snappedSteps
    = direction > 0 ? Math.floor(rawSteps / stepSize) : Math.ceil(rawSteps / stepSize)
  return base + snappedSteps * stepSize
}

export function toValidatedNumber(
  value: number | null,
  {
    step,
    minWithDefault,
    maxWithDefault,
    minWithZeroDefault,
    format,
    snapOnStep,
    small,
    clamp: shouldClamp,
  }: {
    step: number | undefined
    minWithDefault: number
    maxWithDefault: number
    minWithZeroDefault: number
    format: NumberFormatOptionsWithRounding | undefined
    snapOnStep: boolean
    small: boolean
    clamp: boolean
  },
): number | null {
  if (value === null) {
    return value
  }

  let nextValue = value

  if (step != null && snapOnStep && step !== 0) {
    const base
      = small || minWithDefault === Number.MIN_SAFE_INTEGER ? minWithZeroDefault : minWithDefault

    // Snap before clamping so non-step-aligned boundaries stay reachable.
    nextValue = snapToStep(nextValue, base, step, small ? 'nearest' : 'directional')
  }

  if (shouldClamp) {
    nextValue = clamp(nextValue, minWithDefault, maxWithDefault)
  }

  const roundedValue = removeFloatingPointErrors(nextValue, format)
  return shouldClamp ? clamp(roundedValue, minWithDefault, maxWithDefault) : roundedValue
}
